import React, { useEffect, useMemo, useRef, useState } from "react";
import DeckGL from "@deck.gl/react";
import { WebMercatorViewport } from "@deck.gl/core";
import { TileLayer } from "@deck.gl/geo-layers";
import { BitmapLayer, PolygonLayer } from "@deck.gl/layers";
import { ArrowLeft, Hexagon, Layers3, Map as MapIcon, Radio, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import CaseStudyCta from "../components/layout/CaseStudyCta";
import { may17SyntheticZones } from "../data/may17Zones";
import { arendalFacts } from "../data/arendalsuka";
import "../styles/may17.css";

// Four shades of one green, light to dark, for the synthetic activity steps 1 to 4.
const shades = [
  [212, 239, 225],
  [143, 214, 181],
  [59, 181, 138],
  [14, 122, 87],
];

// Each stage lowers every zone's synthetic step by `drop` (never below the lightest).
// `looks` describes the resulting map for screen readers, so keep it in step with `drop`.
const stages = [
  { id: "early", label: "Early", state: "Quiet", drop: 3, looks: "every zone in the lightest shade" },
  { id: "arrival", label: "Arrival", state: "Building", drop: 1, looks: "the three middle zones in the second-darkest shade, lighter towards the edges" },
  { id: "celebration", label: "Celebration", state: "Busy", drop: 0, looks: "the three middle zones in the darkest shade, lighter towards the edges" },
  { id: "departure", label: "Departure", state: "Easing", drop: 2, looks: "the three middle zones in the second-lightest shade and every other zone in the lightest shade" },
];

const M_PER_DEG_LAT = 111200;

// Pointy-topped hexagon with `radius` metres from centre to each corner.
const hexagon = ([lng, lat], radius) => {
  const mPerDegLng = 111320 * Math.cos((lat * Math.PI) / 180);
  return Array.from({ length: 6 }, (_, k) => {
    const angle = Math.PI / 6 + (k * Math.PI) / 3;
    return [lng + (radius * Math.cos(angle)) / mPerDegLng, lat + (radius * Math.sin(angle)) / M_PER_DEG_LAT];
  });
};

const zones = may17SyntheticZones.map(zone => ({
  ...zone,
  polygon: hexagon(zone.position, zone.radius),
  step: Math.min(4, Math.max(1, Math.round(zone.level * 4))),
}));

const corners = zones.flatMap(zone => zone.polygon);
const zoneBounds = [
  [Math.min(...corners.map(c => c[0])), Math.min(...corners.map(c => c[1]))],
  [Math.max(...corners.map(c => c[0])), Math.max(...corners.map(c => c[1]))],
];

const MAP_HEIGHT = 400;

// Fit the whole grid below the key at whatever width the map is shown at.
const fitView = width => {
  const narrow = width < 480;
  const { longitude, latitude, zoom } = new WebMercatorViewport({ width, height: MAP_HEIGHT }).fitBounds(zoneBounds, {
    padding: { top: narrow ? 86 : 62, bottom: 14, left: 12, right: 12 },
  });
  return { longitude, latitude, zoom, pitch: 0, bearing: 0 };
};

const useWidth = fallback => {
  const ref = useRef(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => {
      const w = Math.round(el.getBoundingClientRect().width);
      if (w > 0) setWidth(w);
    };
    update();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const AbstractCityMap = ({ stage }) => {
  const [ref, width] = useWidth(520);
  const viewState = useMemo(() => fitView(Math.max(width, 240)), [width]);
  const animate = useMemo(() => !prefersReducedMotion(), []);

  const layers = [
    new TileLayer({
      id: "may17-osm",
      data: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
      minZoom: 0,
      maxZoom: 19,
      tileSize: 256,
      renderSubLayers: props => {
        const { west, south, east, north } = props.tile.bbox;
        return new BitmapLayer({ id: props.id, image: props.data, bounds: [west, south, east, north] });
      },
    }),
    new PolygonLayer({
      id: "may17-synthetic-zones",
      data: zones,
      getPolygon: d => d.polygon,
      getFillColor: d => [...shades[Math.max(1, d.step - stage.drop) - 1], 210],
      getLineColor: [14, 122, 87, 150],
      lineWidthMinPixels: 1,
      filled: true,
      stroked: true,
      pickable: false,
      updateTriggers: { getFillColor: stage.id },
      transitions: animate ? { getFillColor: 450 } : undefined,
    }),
  ];

  return (
    <figure className="may17-map">
      <div
        ref={ref}
        className="may17-map-canvas"
        role="img"
        aria-label={`Synthetic map of central Oslo divided into ${zones.length} hexagonal zones, showing the ${stage.label.toLowerCase()} stage (${stage.state.toLowerCase()}): ${stage.looks}.`}
      >
        <DeckGL initialViewState={viewState} controller={false} layers={layers} />
      </div>
      <div className="may17-map-key">
        <span>Relative activity</span>
        <span className="may17-key-scale">
          <small>Lower</small>
          {shades.map(rgb => <i key={rgb.join()} aria-hidden="true" style={{ background: `rgb(${rgb.join(",")})` }} />)}
          <small>Higher</small>
        </span>
        <b>Synthetic data</b>
      </div>
      <figcaption>
        The zones and shading are synthetic and contain no measurements from 17 May. Basemap ©{" "}
        <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap contributors</a>.
      </figcaption>
    </figure>
  );
};

const May17Page = () => {
  const [stageId, setStageId] = useState("celebration");
  const stage = stages.find(s => s.id === stageId);

  return (
    <div className="may17-page">
      <Navbar />
      <main className="pt-16">
        <header className="may17-hero">
          <Link to="/projects" className="may17-back"><ArrowLeft size={15} aria-hidden="true" /> Projects</Link>
          <div className="may17-hero-inner">
            <div className="may17-hero-copy">
              <p className="may17-kicker">Event readiness · Oslo, Norway</p>
              <h1>How central Oslo fills and empties on <span>17 May</span></h1>
              <p className="may17-deck">On 17 May, Norway's Constitution Day, the children's parade, bands and visitors fill the centre of Oslo. We show how busy each part of the centre is as the day goes on, using aggregated mobile-network data.</p>
              <ul className="may17-meta">
                <li><MapIcon size={15} aria-hidden="true" />Central Oslo</li>
                <li><Hexagon size={15} aria-hidden="true" />Broad zones</li>
                <li><Radio size={15} aria-hidden="true" />Readings every 10 minutes</li>
              </ul>
            </div>
          </div>
          <figure className="may17-hero-photo">
            <img src="/may17-oslo-parade.jpg" alt="Constitution Day parade on Karl Johans gate in Oslo, with Norwegian flags lining the street and crowds filling it" />
            <figcaption>
              <span className="may17-credit-place">Karl Johans gate, 17 May 2010</span>
              <span>
                Photo: <a href="https://commons.wikimedia.org/wiki/File:Constitution_Day_parade_passing_by_the_Domus_Bibliotheca_and_Domus_Academica,_University_of_Oslo_-_20100517-01.jpg" target="_blank" rel="noreferrer">Bjoertvedt / Wikimedia Commons</a>
                <span aria-hidden="true"> · </span>
                <a href="https://creativecommons.org/licenses/by-sa/3.0/no/deed.en" target="_blank" rel="noreferrer">CC BY-SA 3.0 NO</a>
              </span>
            </figcaption>
          </figure>
        </header>

        <section className="may17-intro may17-wrap">
          <h2>What changes in central Oslo on {"17 May"}</h2>
          <div>
            <p>Street closures, public transport changes, visitors, and the children's parade all alter how central Oslo works.</p>
            <p>Crowds gather in different parts of the centre at different times of day, and an ordinary day's pattern is no guide. Our view follows that change zone by zone, so everyone planning the day can work from the same map.</p>
          </div>
        </section>

        <section className="may17-picture">
          <div className="may17-wrap may17-picture-grid">
            <div className="may17-picture-copy">
              <h2>Central Oslo through the day, zone by zone</h2>
              <p>We split the centre into broad zones and shade each one by how busy it is. Choose a stage of the day to see the centre go from quiet to busy and back.</p>
              <p className="may17-stage-label" id="may17-stage-label">Stage shown on the map</p>
              <div className="may17-statuses" role="group" aria-labelledby="may17-stage-label">
                {stages.map(s => (
                  <button key={s.id} type="button" aria-pressed={s.id === stageId} onClick={() => setStageId(s.id)}>
                    <small>{s.label}</small>
                    <strong>{s.state}</strong>
                  </button>
                ))}
              </div>
            </div>
            <AbstractCityMap stage={stage} />
          </div>
        </section>

        <section className="may17-decisions may17-wrap">
          <div className="may17-decisions-head"><h2>What the view answers</h2></div>
          <div className="may17-cards">
            <article><Radio aria-hidden="true" /><h3>What is changing?</h3><p>Which zones are filling up, which are at their busiest and which are starting to empty, updated through the day.</p></article>
            <article><Layers3 aria-hidden="true" /><h3>Who needs to know?</h3><p>Transport, safety and event teams can follow the same map during the day and review it together afterwards.</p></article>
            <article><ShieldCheck aria-hidden="true" /><h3>What can be shared?</h3><p>Broad zones and simple labels such as quiet or busy can be shared widely. Counts and exact locations can be kept to the teams that need them.</p></article>
          </div>
        </section>

        <CaseStudyCta
          question="Planning a public event that changes how your city centre works?"
          body="Tell us the area and the day, and we will show you what this view would look like for it."
          extra={
            <p className="text-ink-muted">
              For an event with published figures, see{" "}
              <Link to="/projects/arendalsuka" className="font-semibold text-brand-700 underline underline-offset-2">Arendalsuka</Link>
              , where estimated presence in Arendal sentrum peaked at {arendalFacts.lift} times the July median.
            </p>
          }
        />

        <footer className="may17-method">
          <strong>About the figures</strong>
          <p>The map is synthetic. Its zones are an even grid about 1 km across, and the shading for each stage of the day was set by hand in four steps, darkest in the middle. The photograph shows the parade on Karl Johans gate on 17 May 2010.</p>
        </footer>
      </main>
    </div>
  );
};

export default May17Page;
